import { OrderDetailsEntity, OrderEntity } from 'api/types/entity';
import { yesOrNotOption } from 'helpers';
import { TFunc } from 'libs/i18n';
import { OrderDetailsFormType } from 'modules/order/common/forms/type';
import { DeepPartial } from 'types/deepPartial';

import { OrderFormType } from '../forms/orderForm';

const parseDetails = (details: OrderDetailsEntity): OrderDetailsFormType => ({
  custodianOrganization: {
    value: details.custodianOrganization.value,
    label: details.custodianOrganization.value,
    record: {
      isNew: details.custodianOrganization.isNew,
      id: details.custodianOrganization.referenceRecordId,
    },
  },
  accountNumber: details.accountNumber,
  accountOwnerName: details.accountOwnerName!,
  brokerName: {
    value: details.brokerName.value,
    label: details.brokerName.value,
    record: {
      isNew: details.brokerName.isNew,
      id: details.brokerName.referenceRecordId,
    },
  },
  custodianOrganizationCode: details.custodianOrganizationCode,
});

export const getOrderInitialValue = (
  t: TFunc,
  order: OrderEntity
): DeepPartial<OrderFormType> => ({
  fmNumber: order?.fmNumber,
  manager: order?.manager,
  transactionVenue: order?.transactionVenue
    ? {
        value: order.transactionVenue,
        label: t(
          `order.sections.executionHistorySection.fields.transactionVenue.options.${order.transactionVenue}`
        ),
      }
    : undefined,
  marketType: order?.marketType
    ? {
        value: order?.marketType,
        label: t(`order.marketType.${order.marketType}`),
      }
    : undefined,

  settlementCondition: order?.settlementCondition
    ? {
        value: order?.settlementCondition,
        label: t(
          `order.sections.executionHistorySection.fields.settlementCondition.options.${order.settlementCondition}`
        ),
      }
    : undefined,
  hasBrokerRecommendation: yesOrNotOption(t, order?.hasBrokerRecommendation),
  comment: order?.comment,
  adminDocuments: order?.adminDocuments,

  startDate: order?.startDate,
  endDate: order?.endDate,
  executionPeriodInBusinessDays: order?.executionPeriodInBusinessDays,
  executionTerms: {
    orderType: order?.executionTerms.orderType
      ? {
          value: order.executionTerms.orderType,
          label: t(
            `order.executionTermsOrderType.${order.executionTerms.orderType}`
          ),
        }
      : undefined,
    yield: order?.executionTerms.yield ?? undefined,
    additionalNotes: order?.executionTerms.additionalNotes ?? undefined,
    operationKind: order?.executionTerms.operationKind
      ? {
          value: order.executionTerms.operationKind,
          label: t(
            `order.sections.executionTermsSection.fields.operationKind.types.${order.executionTerms.operationKind}`
          ),
        }
      : undefined,
    operationPurpose: order?.executionTerms.operationPurpose ?? undefined,
  },
  financialInstrument: {
    isin: {
      value: order?.financialInstrument?.isin?.value,
      label: order?.financialInstrument?.isin?.value,
      record: {
        id: order?.financialInstrument?.isin?.referenceRecordId,
        isNew: order.financialInstrument?.isin?.isNew,
      },
    },
    securityType: order?.financialInstrument?.securityType ?? undefined,
    issuer: order?.financialInstrument?.issuer ?? undefined,
    nominalValue: order?.financialInstrument?.nominalValue,
  },
  transactionVolume: order?.transactionVolume
    ? {
        quantity: order.transactionVolume.quantity,
        unitPrice: order.transactionVolume.unitPrice,
        currency: {
          value: order.transactionVolume.currency!,
          label: order.transactionVolume.currency!,
        },
        transactionAmount: order.transactionVolume.transactionAmount,
      }
    : undefined,
  transferVolume: order.transferVolume
    ? {
        quantity: order.transferVolume.quantity,
        unitPrice: order.transferVolume.unitPrice,
        currency: order.transferVolume.currency
          ? {
              value: order.transferVolume.currency,
              label: order.transferVolume.currency,
            }
          : null,
        transactionAmount: order.transferVolume.transactionAmount,
      }
    : undefined,
  confirmationDocument: order.confirmationDocument
    ? {
        contractNumber: order.confirmationDocument?.contractNumber ?? undefined,
        contractDate: order.confirmationDocument?.contractDate ?? undefined,
        files: order.confirmationDocument?.files,
      }
    : undefined,
  senderDetails: order.senderDetails
    ? parseDetails(order.senderDetails)
    : undefined,

  recipientDetails: order.recipientDetails
    ? parseDetails(order.recipientDetails)
    : undefined,
  additionalParameters: {
    transactionDate: order.additionalParameters?.transactionDate ?? undefined,
    settlementDate: order.additionalParameters?.settlementDate ?? undefined,
  },
});
